import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Split and format multiple phone numbers
export const splitPhoneNumbers = (phoneString) => {
  if (!phoneString || phoneString === 'N/A') return [];
  
  // Split by slash or forward slash with optional spaces
  const numbers = phoneString.split(/\s*\/\s*/);
  return numbers.map(num => num.trim()).filter(Boolean);
};

// Enhanced format phone number function
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check for minimum length
  if (cleaned.length < 10) {
    return cleaned; // Return without formatting if too short
  }
  
  // Handle South African numbers
  if (cleaned.startsWith('0')) {
    cleaned = '27' + cleaned.substring(1);
  }
  
  // Add + prefix if not present
  if (!cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  return cleaned;
};

// Validate phone number
export const isValidPhoneNumber = (phoneNumber) => {
  const formattedNumber = formatPhoneNumber(phoneNumber);
  // Basic validation for South African numbers
  const saPhoneRegex = /^\+27[1-9][0-9]{8}$/;
  return saPhoneRegex.test(formattedNumber);
};

// Test WhatsApp link
export const testWhatsAppLink = async (phoneNumber) => {
  const formattedNumber = formatPhoneNumber(phoneNumber);
  try {
    const response = await fetch(`https://wa.me/${formattedNumber.substring(1)}`);
    return response.ok;
  } catch (error) {
    return false;
  }
};

// Bulk validate and update phone numbers in Firestore
export const validateAndUpdatePhoneNumbers = async () => {
  const results = {
    total: 0,
    valid: 0,
    invalid: [],
    updated: 0,
    multipleNumbers: 0 // New counter for providers with multiple numbers
  };

  try {
    const providersRef = collection(db, 'providers');
    const snapshot = await getDocs(providersRef);
    
    results.total = snapshot.size;

    for (const doc of snapshot.docs) {
      const provider = doc.data();
      const originalNumber = provider.phoneNumber;

      if (!originalNumber || originalNumber === 'N/A') {
        results.invalid.push({
          providerId: doc.id,
          providerName: provider.name,
          originalNumber: 'No number provided',
          type: 'missing'
        });
        continue;
      }

      // Split multiple numbers
      const numbers = splitPhoneNumbers(originalNumber);
      
      if (numbers.length > 1) {
        results.multipleNumbers++;
        
        // Update document with separated numbers
        try {
          await updateDoc(doc.ref, {
            phoneNumber: formatPhoneNumber(numbers[0]),
            phoneNumber2: formatPhoneNumber(numbers[1])
          });
          results.updated++;
          
          // Validate both numbers
          const isFirstValid = isValidPhoneNumber(formatPhoneNumber(numbers[0]));
          const isSecondValid = isValidPhoneNumber(formatPhoneNumber(numbers[1]));
          
          if (isFirstValid && isSecondValid) {
            results.valid += 2;
          } else {
            if (!isFirstValid) {
              results.invalid.push({
                providerId: doc.id,
                providerName: provider.name,
                originalNumber: numbers[0],
                type: 'primary'
              });
            }
            if (!isSecondValid) {
              results.invalid.push({
                providerId: doc.id,
                providerName: provider.name,
                originalNumber: numbers[1],
                type: 'secondary'
              });
            }
          }
        } catch (error) {
          console.error(`Error updating multiple numbers for ${provider.name}:`, error);
        }
      } else {
        // Handle single number case
        const formattedNumber = formatPhoneNumber(numbers[0]);
        const isValid = isValidPhoneNumber(formattedNumber);

        if (isValid) {
          results.valid++;
          if (originalNumber !== formattedNumber) {
            await updateDoc(doc.ref, {
              phoneNumber: formattedNumber
            });
            results.updated++;
          }
        } else {
          results.invalid.push({
            providerId: doc.id,
            providerName: provider.name,
            originalNumber: numbers[0],
            type: 'single'
          });
        }
      }
    }

    return results;

  } catch (error) {
    console.error('Error validating phone numbers:', error);
    throw error;
  }
};

// Add these new utility functions
export const isLandlineNumber = (phoneNumber) => {
  const formatted = formatPhoneNumber(phoneNumber);
  return formatted.startsWith('+2711') || formatted.startsWith('+2712');
};

export const isValidLength = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  return cleaned.length >= 10;
}; 